/**
 * Module 6I — Admin AI diagnostics page.
 *
 * `/admin/ai-diagnostics` — surfaces the top unanswered Ask AI questions
 * across all tenants over a rolling window. This is the feedback loop:
 * questions the LLM couldn't route (`unknown_intent`), envelopes the
 * semantic layer rejected (`envelope_rejected`), and executor errors.
 *
 * Column semantics:
 *   • "why rejected" is the aggregated top reason string from the
 *     backend — envelope_reason for semantic rejections, classifier
 *     reason for unknown_intent, or "—" if the row had none.
 *   • "studios" is the DISTINCT tenant count — signals whether a gap is
 *     universal (fix soon) or isolated to one tenant (probably a typo).
 */
import { useEffect, useState } from "react";
import { adminApi } from "@/lib/admin_auth";
import { Sparkles, RefreshCw } from "lucide-react";


export function AIDiagnosticsView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(20);

  const load = () => {
    setBusy(true); setError(null);
    adminApi.get(`/admin/ai/unanswered?days=${days}&limit=${limit}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load diagnostics"))
      .finally(() => setBusy(false));
  };
  useEffect(load, [days, limit]);   // reload when the window changes

  return (
    <div data-testid="admin-ai-diagnostics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-400/80 mb-1">
            <Sparkles size={11} className="inline mr-1" /> Feedback loop
          </div>
          <div className="text-lg text-zinc-100">Unanswered Ask AI questions</div>
          <div className="text-xs text-zinc-500 mt-1">
            Cross-tenant view of questions the classifier or semantic layer couldn't answer.
            Sorted by frequency — top rows are the next intents worth building.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            data-testid="ai-diag-window-select"
            className="bg-zinc-950 border border-zinc-800 text-xs px-2 py-1.5 text-zinc-200"
          >
            <option value={1}>Last 1 day</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            data-testid="ai-diag-limit-select"
            className="bg-zinc-950 border border-zinc-800 text-xs px-2 py-1.5 text-zinc-200"
          >
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>
          <button
            onClick={load}
            disabled={busy}
            data-testid="ai-diag-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-700 hover:bg-zinc-900 text-zinc-300 disabled:opacity-50"
          >
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="text-sm text-red-400 border border-red-900 bg-red-950/40 p-3">{error}</div>
      ) : !data ? (
        <div className="text-xs text-zinc-500 py-8">Loading…</div>
      ) : data.items.length === 0 ? (
        <EmptyState days={days} />
      ) : (
        <>
          <SummaryBar data={data} />
          <UnansweredTable items={data.items} />
        </>
      )}
    </div>
  );
}


function EmptyState({ days }) {
  return (
    <div className="text-xs text-zinc-500 py-12 border border-dashed border-zinc-800 text-center"
         data-testid="ai-diag-empty">
      <Sparkles size={14} className="inline mr-1.5 text-emerald-500/60" />
      No unanswered questions in the last {days} days — either the classifier is nailing everything, or nobody's asking.
    </div>
  );
}


function SummaryBar({ data }) {
  return (
    <div className="grid grid-cols-3 gap-3 mb-5 text-xs">
      <Stat label="Total failure rows" value={data.total_failure_rows} />
      <Stat label="Distinct questions" value={data.unique_questions} />
      <Stat
        label="Scan window"
        value={`${data.days} day${data.days === 1 ? "" : "s"}${data.cap_hit ? " · CAP HIT" : ""}`}
        alert={data.cap_hit}
      />
    </div>
  );
}


function Stat({ label, value, alert }) {
  return (
    <div className={`border ${alert ? "border-amber-800 bg-amber-950/20" : "border-zinc-900"} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm mt-0.5 ${alert ? "text-amber-300" : "text-zinc-200"}`}>{value}</div>
    </div>
  );
}


function UnansweredTable({ items }) {
  return (
    <div className="border border-zinc-900">
      <table className="w-full text-xs" data-testid="ai-diag-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-900">
            <th className="px-3 py-2 w-12">#</th>
            <th className="px-3 py-2">Question (normalized)</th>
            <th className="px-3 py-2 w-16 text-right">Count</th>
            <th className="px-3 py-2 w-20 text-right">Studios</th>
            <th className="px-3 py-2 w-36">Last seen</th>
            <th className="px-3 py-2 w-72">Why rejected</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <UnansweredRow key={row.normalized_query} row={row} index={i + 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function UnansweredRow({ row, index }) {
  const [open, setOpen] = useState(false);
  const topReason = row.top_reasons?.[0]?.reason || "—";
  const statuses = Object.entries(row.statuses || {}).map(([k, v]) => `${k} (${v})`).join(", ");
  return (
    <>
      <tr
        onClick={() => setOpen(o => !o)}
        data-testid={`ai-diag-row-${index}`}
        className="border-b border-zinc-900 hover:bg-zinc-950 cursor-pointer"
      >
        <td className="px-3 py-2 text-zinc-600">{index}</td>
        <td className="px-3 py-2 text-zinc-200 font-mono truncate max-w-[300px]" title={row.normalized_query}>
          {row.normalized_query}
        </td>
        <td className="px-3 py-2 text-right text-zinc-200">{row.count}</td>
        <td className="px-3 py-2 text-right text-zinc-400">{row.studio_count}</td>
        <td className="px-3 py-2 text-zinc-500">{(row.last_seen || "").slice(0, 16)}</td>
        <td className="px-3 py-2 text-zinc-400 truncate max-w-[280px]" title={topReason}>
          {topReason}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-zinc-900 bg-zinc-950/60">
          <td colSpan={6} className="px-6 py-3 text-[11px] text-zinc-400" data-testid={`ai-diag-detail-${index}`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Raw examples</div>
                <ul className="space-y-1 font-mono text-zinc-300">
                  {(row.examples || []).map((ex, i) => (
                    <li key={i} className="truncate">· {ex}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">All rejection reasons</div>
                <ul className="space-y-1">
                  {(row.top_reasons || []).map((r, i) => (
                    <li key={i} className="text-zinc-400">
                      <span className="text-zinc-500">{r.count}×</span> {r.reason}
                    </li>
                  ))}
                  {row.top_reasons?.length === 0 ? (
                    <li className="text-zinc-600">No reason strings captured for this bucket.</li>
                  ) : null}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Status breakdown</div>
                <div className="text-zinc-400">{statuses || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">
                  Studios ({row.studio_count})
                </div>
                <div className="text-zinc-400 truncate">
                  {(row.studio_ids || []).slice(0, 5).join(", ")}
                  {row.studio_count > 5 ? ` + ${row.studio_count - 5} more` : ""}
                </div>
              </div>
              {row.sample_intents?.length ? (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-zinc-500 mb-1">
                    LLM attempted routing
                  </div>
                  <div className="text-zinc-400 font-mono">{row.sample_intents.join(", ")}</div>
                </div>
              ) : null}
              <div className="col-span-2 text-[10px] text-zinc-600 mt-1">
                First seen: {(row.first_seen || "").slice(0, 16)}
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
